import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CollaborativeEditor } from '../../../shared/ui/CollaborativeEditor/CollaborativeEditor';
// Fase 5.10: `useCurrentUser()` es un selector de un store zustand a nivel de módulo
// (03.1-auth-store.md), no un `useContext` — no acopla re-renders. Este editor se monta desde 2
// cadenas distintas (`DocContent` y, en tasks, `WorkframeDocColumn`); threadear `user` por ambas
// sin ganancia real. Misma excepción que `InlineSocialOutline.jsx` (5.6).
// eslint-disable-next-line no-restricted-imports
import { useCurrentUser } from '../../../context/authHooks';
import { docsApi } from '../api';
import { docToTiptapHtml } from '../lib/docToTiptap';
import LilMenu from './document-text/lilmenu/lilmenu';
import TableMenu from './document-text/tablemenu/TableMenu';
import { StickyTableHeadersOverlay } from './document-text/tablemenu/StickyTableHeadersOverlay';
import { rgbToHex, hexToRgbStr } from '../lib/docLinkColor';
import FloatingCommentsPanel from './comments/FloatingCommentsPanel';
import { DatabaseExtension } from './extensions/DatabaseExtension';
import { DocTagExtension } from './extensions/DocTagExtension';
import { MentionExtension } from './extensions/MentionExtension';
import { ColumnExtension, ColumnContainerExtension } from './extensions/ColumnExtension';
import { ResizableImageExtension } from './extensions/ImageExtension';
import { InlineSocialOutlineExtension } from './extensions/InlineSocialOutlineExtension';
import { InlineFrameExtension } from './extensions/InlineFrameExtension';
import { EmbedPlaceholderExtension } from './extensions/EmbedPlaceholderExtension';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import './DocsCollaborativeEditor.css';

// Editor colaborativo de documentos (integración main→dev, epic D2 — Opción A). Reemplaza el
// motor clásico `DoctexHtml.jsx` (contentEditable + `createRoot`). Compone el
// `CollaborativeEditor` compartido (Tiptap + Yjs + Hocuspocus) y le añade las extensiones de
// documento + el toolbar flotante `LilMenu`.
//
// `DatabaseExtension` / `EmbedPlaceholderExtension` renderizan un placeholder — la tabla /
// flujo / ops-table vivos se restauran en D2.5 (regresión aceptada por el usuario).
//
// Guard anti-escritura: el editor NO persiste (`onSave`) hasta que el HTML difiere del que se
// cargó — así abrir un doc viejo (y su conversión de tokens/marcadores) nunca lo reescribe;
// solo la primera edición real del usuario lo hace.

export default function DocsCollaborativeEditor({
    docId,
    spaceId,
    content,
    onSave,
    canEdit = true,
    onActiveUsersChange,
    placeholder = 'Escribe aquí...',
}) {
    const { id, dbId, name, image } = useCurrentUser();
    const currentUser = useMemo(() => ({ id: id || dbId, dbId, nick: name, name, image, avatar_url: image }), [id, dbId, name, image]);
    const initialHtml = useMemo(() => docToTiptapHtml(content, { docId }), [content, docId]);

    const editorRef = useRef(null);
    const baselineRef = useRef(null);
    const saveTimeoutRef = useRef(null);
    const lastClickedLink = useRef(0);

    const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
    const [lilMenu, setLilMenu] = useState({ isOpen: false, position: { top: 0, left: 0 } });
    const [tableMenu, setTableMenu] = useState({ isOpen: false, position: null });
    const [linkContext, setLinkContext] = useState(null);

    const [editor, setEditor] = useState(null);

    const customExtensions = useMemo(() => [
        DatabaseExtension,
        DocTagExtension,
        MentionExtension,
        ColumnContainerExtension,
        ColumnExtension,
        ResizableImageExtension,
        InlineFrameExtension.configure({ currentUser }),
        EmbedPlaceholderExtension,
        InlineSocialOutlineExtension.configure({ docId, spaceId }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
    ], [docId, spaceId]);

    useEffect(() => () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); }, []);

    const handleEditorReady = useCallback((ed) => {
        editorRef.current = ed;
        setEditor(ed);
        // Baseline para el guard anti-escritura — lo que quedó tras hidratar.
        setTimeout(() => { baselineRef.current = ed?.getHTML?.() ?? null; }, 0);
    }, []);

    const handleUpdate = useCallback((html) => {
        if (!canEdit) return;
        if (baselineRef.current === null) { baselineRef.current = html; return; }
        if (html === baselineRef.current) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => { onSave(html); }, 1000);
    }, [canEdit, onSave]);

    const handleUpload = useCallback(async (file, ed) => {
        if (!canEdit) return;
        try {
            const res = await docsApi.uploadEmbed(file);
            if (res?.success && res.url && ed?.chain) {
                ed.chain().focus().setImage({ src: res.url }).run();
            }
        } catch (err) {
            console.error('[DocsCollaborativeEditor] upload error', err);
        }
    }, [canEdit]);

    // ── LilMenu (toolbar flotante en selección) — portado de `main`'s DocTiptapEditor ──
    const checkSelection = useCallback(() => {
        if (!editor || !canEdit) return;
        const sel = window.getSelection();
        if (!sel) { 
            setLilMenu((p) => (p.isOpen ? { ...p, isOpen: false } : p)); 
            setTableMenu((p) => (p.isOpen ? { ...p, isOpen: false } : p));
            return; 
        }
        const editorDom = editor.view?.dom;
        if (!editorDom) return;
        if (sel.anchorNode) {
            const el = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
            if (el?.closest('.lilmenu-container') || el?.closest('.tablemenu-container')) return;
        }
        if (Date.now() - lastClickedLink.current < 200) return;

        const clampLeft = (rawLeft, scrollX) => {
            const half = 180; const margin = 10;
            let left = rawLeft;
            if (left - scrollX < half + margin) left = scrollX + half + margin;
            else if (left - scrollX + half > window.innerWidth - margin) left = scrollX + window.innerWidth - half - margin;
            return left;
        };

        let newLilMenu = { isOpen: false, position: { top: 0, left: 0 } };
        let newLinkContext = null;
        let hasSelection = false;

        if (sel.isCollapsed) {
            if (editorDom.contains(sel.anchorNode)) {
                let node = sel.anchorNode; let anchorEl = null;
                while (node && node !== editorDom) {
                    if (node.nodeType === 1 && node.tagName === 'A') { anchorEl = node; break; }
                    node = node.parentNode;
                }
                if (anchorEl) {
                    const rect = anchorEl.getBoundingClientRect();
                    let top = rect.top + window.scrollY - 20;
                    if (rect.top < 60) top = rect.bottom + window.scrollY + 54;
                    const left = clampLeft(rect.left + window.scrollX + rect.width / 2, window.scrollX);
                    newLinkContext = { href: anchorEl.href, text: anchorEl.textContent, color: rgbToHex(anchorEl.style.color) || '#4CAF50', element: anchorEl };
                    newLilMenu = { isOpen: true, position: { top, left } };
                }
            }
        } else if (editorDom.contains(sel.anchorNode) && sel.toString().trim().length > 0) {
            hasSelection = true;
            const rect = sel.getRangeAt(0).getBoundingClientRect();
            if (rect.width !== 0 || rect.height !== 0) {
                let top = rect.top + window.scrollY - 20;
                if (rect.top < 60) top = rect.bottom + window.scrollY + 54;
                const left = clampLeft(rect.left + window.scrollX + rect.width / 2, window.scrollX);
                newLilMenu = { isOpen: true, position: { top, left } };
            }
        }

        setLinkContext(newLinkContext);
        setLilMenu(newLilMenu);

        if (editor.isActive('table')) {
            if (newLilMenu.isOpen) {
                setTableMenu({ isOpen: true, position: { top: newLilMenu.position.top + 46, left: newLilMenu.position.left } });
            } else {
                let node = sel.anchorNode;
                let cellEl = null;
                while (node && node !== editorDom) {
                    if (node.nodeType === 1 && (node.tagName === 'TD' || node.tagName === 'TH')) { cellEl = node; break; }
                    node = node.parentNode;
                }
                if (cellEl) {
                    const rect = cellEl.getBoundingClientRect();
                    let top = rect.top + window.scrollY - 46;
                    if (rect.top < 60) top = rect.bottom + window.scrollY + 10;
                    const left = clampLeft(rect.left + window.scrollX + rect.width / 2, window.scrollX);
                    setTableMenu({ isOpen: true, position: { top, left } });
                } else {
                    setTableMenu({ isOpen: false, position: null });
                }
            }
        } else {
            setTableMenu({ isOpen: false, position: null });
        }
    }, [editor, canEdit]);

    useEffect(() => {
        document.addEventListener('selectionchange', checkSelection);
        return () => document.removeEventListener('selectionchange', checkSelection);
    }, [checkSelection]);

    const handleEditorClick = (e) => {
        const anchorEl = e.target.closest && e.target.closest('a');
        if (!canEdit) {
            if (anchorEl?.href && anchorEl.classList.contains('external')) window.open(anchorEl.href, '_blank', 'noopener,noreferrer');
            return;
        }
        if (anchorEl && editor) {
            e.preventDefault();
            lastClickedLink.current = Date.now();
            const rect = anchorEl.getBoundingClientRect();
            let top = rect.top + window.scrollY - 20;
            if (rect.top < 60) top = rect.bottom + window.scrollY + 54;
            const left = rect.left + window.scrollX + rect.width / 2;
            setLinkContext({ href: anchorEl.href, text: anchorEl.textContent, color: rgbToHex(anchorEl.style.color) || '#4CAF50', element: anchorEl });
            setLilMenu({ isOpen: true, position: { top, left } });
        }
    };

    const handleLilMenuSelect = (action, value) => {
        if (!editor || !canEdit) return;
        const c = () => editor.chain().focus();
        if (action === 'format') {
            const map = { bold: () => c().toggleBold().run(), italic: () => c().toggleItalic().run(), underline: () => c().toggleUnderline().run(), strikeThrough: () => c().toggleStrike().run(), quote: () => c().toggleBlockquote().run(), removeFormat: () => c().unsetAllMarks().clearNodes().run() };
            map[value]?.();
        } else if (action === 'text') {
            c().setParagraph().run();
        } else if (action === 'heading') {
            c().toggleHeading({ level: value }).run();
        } else if (action === 'size') {
            const lvl = { '25px': 1, '22px': 1, '18px': 2, '14px': 3 }[value];
            if (lvl) c().toggleHeading({ level: lvl }).run(); else c().setParagraph().run();
        } else if (action === 'color') {
            c().setColor(value).run();
        } else if (action === 'list') {
            const map = { bullet: () => c().toggleBulletList().run(), numbered: () => c().toggleOrderedList().run(), checklist: () => c().toggleTaskList().run() };
            map[value]?.();
        } else if (action === 'insert-block') {
            if (value === 'code') c().toggleCodeBlock().run();
            else if (value === 'bullet') c().toggleBulletList().run();
            else if (value === 'numbered') c().toggleOrderedList().run();
            else if (value === 'columns') c().insertContent('<div class="doc-column-container"><div class="doc-column"><p>Columna 1</p></div><div class="doc-column"><p>Columna 2</p></div></div>').run();
            else if (value === 'table') c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
            else if (value === 'toggle') c().insertContent('<details><summary>Lista contraíble</summary><p>Contenido...</p></details>').run();
            else if (value === 'inline-social-outline') c().insertContent({ type: 'inlineSocialOutline', attrs: { id: `social-outline-${Date.now()}`, outlineState: null, docId, spaceId } }).run();
            else if (value === 'inline-frame') c().insertContent({ type: 'inlineFrame', attrs: { id: `inline-frame-${Date.now()}`, createdBy: currentUser.id } }).run();
        } else if (action === 'link') {
            if (value && typeof value === 'object') {
                const { url, text: linkText, color } = value;
                if (!url) return;
                if (linkContext?.element) {
                    linkContext.element.href = url;
                    if (color) {
                        const rgb = hexToRgbStr(color);
                        linkContext.element.style.color = color;
                        linkContext.element.style.background = `rgba(${rgb}, 0.15)`;
                        linkContext.element.style.border = `1px solid rgba(${rgb}, 0.2)`;
                    }
                    if (linkText) {
                        const span = linkContext.element.querySelector('.doc-tag-text');
                        if (span) span.textContent = linkText; else linkContext.element.textContent = linkText;
                    }
                    editor.commands.focus();
                } else {
                    const display = linkText || url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
                    let styleStr = '';
                    if (color) {
                        const rgb = hexToRgbStr(color);
                        styleStr = `style="color:${color};background:rgba(${rgb},0.15);border:1px solid rgba(${rgb},0.2);text-decoration:none;border-radius:4px;padding:1px 6px;font-weight:500;"`;
                    }
                    c().insertContent(`&nbsp;<a href="${url}" target="_blank" class="doc-tag external" ${styleStr}><span class="doc-tag-emoji">🔗</span> <span class="doc-tag-text">${display}</span></a>&nbsp;`).run();
                }
            } else if (value) {
                c().setLink({ href: value }).run();
            }
        } else if (action === 'convert') {
            if (value === 'tarea') c().toggleTaskList().run();
            else if (value === 'documento') c().setParagraph().run();
        }
        setLilMenu((p) => ({ ...p, isOpen: false }));
        setLinkContext(null);
    };

    return (
        <div className="doctex-html-wrapper" style={{ position: 'relative' }} onClick={handleEditorClick}>
            {!canEdit && (
                <div className="doctex-readonly-badge" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#888',
                    background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '6px',
                    padding: '4px 10px', marginBottom: '12px', userSelect: 'none',
                }}>
                    <span>👁 Modo solo lectura</span>
                </div>
            )}

            <CollaborativeEditor
                key={docId}
                documentId={`doc-${docId}`}
                initialHtml={initialHtml}
                currentUser={currentUser}
                editable={canEdit}
                className="doctex-rich-editor doctex-tiptap-content"
                customExtensions={customExtensions}
                onEditorReady={handleEditorReady}
                onUpdate={handleUpdate}
                onUpload={handleUpload}
                onActiveUsersChange={onActiveUsersChange}
                onSelectionUpdate={checkSelection}
                placeholder={placeholder}
            />

            <FloatingCommentsPanel docId={docId} />

            <StickyTableHeadersOverlay editor={editor} />

            {canEdit && lilMenu.isOpen && createPortal(
                <LilMenu
                    isOpen={lilMenu.isOpen}
                    position={lilMenu.position}
                    onSelect={handleLilMenuSelect}
                    onClose={() => { setLilMenu((p) => ({ ...p, isOpen: false })); setLinkContext(null); }}
                    linkContext={linkContext}
                />,
                document.body,
            )}
        </div>
    );
}
